import { useState } from 'react';
import GenerateButton from './GenerateButton';
import SignupPrompt from '../../components/ui/SignupPrompt';
import { useUser } from '../../auth/useUser';

export default function LogoForm({
  prompt,
  onPromptChange,
  transparentBackground,
  onTransparentBackgroundChange,
  colorHints,
  onColorHintsChange,
  logoType,
  onLogoTypeChange,
  onGenerate,
  onUploadClick,
  isLoading,
  isUploading,
}) {
  const { isAuthenticated, isGuest, canGenerate, remaining, generationsLimit } = useUser();
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [activeTab, setActiveTab] = useState('create');

  const normalizedColorHints = Array.isArray(colorHints) ? colorHints : [];
  const [hexDrafts, setHexDrafts] = useState(() =>
    normalizedColorHints.slice(0, 3).map((h) => (h?.color || '#000000').toUpperCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeTab === 'upload') return;
    if (!prompt.trim()) return;

    if (!isAuthenticated && !canGenerate) {
      setShowSignupPrompt(true);
      return;
    }

    onGenerate();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.metaKey && prompt.trim() && activeTab === 'create') {
      if (!isAuthenticated && !canGenerate) {
        setShowSignupPrompt(true);
        return;
      }
      onGenerate();
    }
  };

  const addColorHint = () => {
    const next = [...normalizedColorHints, { color: '#000000', purpose: '' }].slice(0, 3);
    onColorHintsChange?.(next);
    setHexDrafts((prev) => [...(Array.isArray(prev) ? prev : []), '#000000'].slice(0, 3));
  };

  const removeColorHint = (idx) => {
    const next = normalizedColorHints.filter((_, i) => i !== idx);
    onColorHintsChange?.(next);
    setHexDrafts((prev) => (Array.isArray(prev) ? prev : []).filter((_, i) => i !== idx));
  };

  const updateColorHint = (idx, patch) => {
    const next = [...normalizedColorHints];
    next[idx] = { ...(next[idx] || { color: '#000000', purpose: '' }), ...patch };
    onColorHintsChange?.(next.slice(0, 3));
  };

  const setHexDraft = (idx, value) => {
    const nextDrafts = [...hexDrafts];
    nextDrafts[idx] = value;
    setHexDrafts(nextDrafts);

    const normalized = (value || '').trim().toUpperCase();
    const fullHex = /^#([0-9A-F]{6})$/;
    if (fullHex.test(normalized)) {
      updateColorHint(idx, { color: normalized });
    }
  };

  return (
    <div className="glass flex flex-col h-full overflow-hidden shadow-2xl shadow-violet-500/5">
      {/* Tab Switcher */}
      <div className="flex bg-slate-100/50 dark:bg-white/[0.02] p-1 m-3 rounded-2xl border border-white/5 gap-1">
        <button
          onClick={() => setActiveTab('create')}
          className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wide transition-all rounded-xl ${activeTab === 'create'
            ? 'bg-white dark:bg-white/10 text-violet-600 dark:text-white shadow-xl shadow-violet-500/10'
            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
        >
          Create New
        </button>
        <button
          onClick={() => setActiveTab('upload')}
          className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wide transition-all rounded-xl ${activeTab === 'upload'
            ? 'bg-white dark:bg-white/10 text-violet-600 dark:text-white shadow-xl shadow-violet-500/10'
            : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
        >
          Upload Own
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-5 custom-scrollbar">
        {activeTab === 'create' ? (
          <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in-up">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label htmlFor="prompt" className="text-sm font-bold text-slate-700 dark:text-slate-200">
                  Icon Prompt
                </label>
                <span className="text-xs font-bold text-slate-400 dark:text-slate-500">
                  {prompt.length}/250
                </span>
              </div>
              <textarea
                id="prompt"
                value={prompt}
                onChange={(e) => onPromptChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., A minimalist mountain peak with a sunrise, modern tech company..."
                rows={3}
                maxLength={250}
                className="textarea focus:ring-violet-500/50 bg-white dark:bg-black/20 border-slate-200 dark:border-white/5 text-slate-900 dark:text-white text-base leading-relaxed placeholder:text-slate-400 dark:placeholder:text-slate-500 min-h-[80px]"
              />
              <div className="flex items-center gap-2 group">
                <div className="px-2 py-1 rounded bg-violet-500/10 text-violet-500 text-[9px] font-black">PRO TIP</div>
                <p className="help flex items-center gap-1.5 font-medium">
                  Press <kbd className="kbd py-0.5">⌘</kbd> + <kbd className="kbd py-0.5">↵</kbd> to generate
                </p>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-4 pt-2 border-t border-slate-200 dark:border-white/5">
              <div className="flex items-center justify-between gap-4 bg-white/30 dark:bg-white/[0.02] p-3 rounded-xl border border-slate-200 dark:border-white/10">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800 dark:text-white mb-0.5">Transparent background</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Best for app icons and stickers</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!transparentBackground}
                  onClick={() => onTransparentBackgroundChange?.(!transparentBackground)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 ${transparentBackground ? 'bg-violet-600 shadow-[0_0_15px_rgba(139,92,246,0.3)]' : 'bg-slate-200 dark:bg-white/10'
                    }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${transparentBackground ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                </button>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">File Format</p>
                <div className="flex bg-slate-100/50 dark:bg-white/[0.02] rounded-xl p-1 gap-1 border border-white/5">
                  {['png', 'webp'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => onLogoTypeChange?.(type)}
                      className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${logoType === type
                        ? 'bg-white dark:bg-white/10 text-violet-600 dark:text-white shadow-lg'
                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                        }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Color hints</p>
                  <button
                    type="button"
                    onClick={addColorHint}
                    disabled={normalizedColorHints.length >= 3}
                    className="text-xs font-bold text-violet-400 hover:text-violet-300 transition-colors disabled:opacity-50"
                  >
                    + Add Color
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-2.5">
                  {normalizedColorHints.map((hint, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-white/40 dark:bg-white/[0.02] rounded-2xl p-3 border border-slate-200 dark:border-white/10 group transition-all hover:bg-white/60 dark:hover:bg-white/[0.05]">
                      <div className="relative h-9 w-9 rounded-xl border-2 border-white/50 dark:border-white/10 overflow-hidden shrink-0 shadow-lg" style={{ backgroundColor: hint?.color }}>
                        <input
                          type="color"
                          value={hint?.color || '#000000'}
                          onChange={(e) => {
                            const next = e.target.value.toUpperCase();
                            updateColorHint(idx, { color: next });
                            setHexDraft(idx, next);
                          }}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        />
                      </div>
                      <input
                        type="text"
                        value={hint?.purpose || ''}
                        onChange={(e) => updateColorHint(idx, { purpose: e.target.value })}
                        placeholder="Color role (e.g. Neon Accents)"
                        className="bg-transparent text-xs font-bold text-slate-700 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 border-none focus:ring-0 p-0 flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeColorHint(idx)}
                        className="text-slate-300 dark:text-slate-700 hover:text-red-500 transition-colors p-1"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {normalizedColorHints.length === 0 && (
                    <div className="text-center py-6 text-slate-400 dark:text-slate-500 text-sm italic">
                      No color hints added
                    </div>
                  )}
                </div>
              </div>
            </div>

            <GenerateButton
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={!prompt.trim()}
            />
          </form>
        ) : (
          <div className="flex flex-col items-center justify-center h-full space-y-8 py-10 animate-fade-in-up">
            <div
              onClick={onUploadClick}
              className="w-full aspect-square max-w-[280px] rounded-[2.5rem] border-2 border-dashed border-violet-500/20 hover:border-violet-500/50 hover:bg-violet-500/5 transition-all duration-500 cursor-pointer flex flex-col items-center justify-center p-10 text-center group relative overflow-hidden shadow-2xl shadow-violet-500/5"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="w-20 h-20 rounded-3xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl group-hover:shadow-violet-500/20">
                <svg className="w-10 h-10 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
              <p className="text-lg font-black text-slate-900 dark:text-white tracking-tight">Drop your concept</p>
              <p className="help mt-3 font-medium leading-relaxed">Studio-grade optimization for<br />PNG or JPG assets up to 10MB.</p>
            </div>

            <div className="w-full space-y-4">
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 text-center uppercase tracking-[0.3em]">Protocol</p>
              <div className="space-y-3">
                {[
                  "Upload a raw concept or sketch",
                  "AI professionally remaster its geometry",
                  "Deploy high-fidelity vector-ready assets"
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-4 bg-white/40 dark:bg-white/[0.02] border border-white/10 rounded-2xl p-4 transition-all hover:bg-white/60 dark:hover:bg-white/[0.05]">
                    <span className="w-6 h-6 rounded-lg bg-violet-500/10 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 text-[10px] flex items-center justify-center font-black">
                      0{i + 1}
                    </span>
                    <p className="text-xs font-black text-slate-700 dark:text-white uppercase tracking-wider">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={onUploadClick}
              className="btn-primary w-full py-5 text-sm font-black uppercase tracking-[0.2em] shadow-2xl shadow-violet-500/30"
              disabled={isUploading}
            >
              {isUploading ? 'Syncing...' : 'Select Source'}
            </button>
          </div>
        )}
      </div>


      <SignupPrompt
        isOpen={showSignupPrompt}
        onClose={() => setShowSignupPrompt(false)}
        message={`You've used all ${generationsLimit} free generations. Sign up for more generations!`}
      />
    </div>
  );
}
